import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  ScanLine, Clock, Database, MapPinned, Users, Trophy,
  Ruler, Network, RefreshCw, Target, ArrowRight, ExternalLink, Sparkles,
  Bone, Apple, Brain, Smile, PersonStanding
} from 'lucide-react';

const dimensions = [
  { icon: Bone, color: 'var(--brand-primary)', accent: 'var(--brand-secondary)', title: 'Skeletal Growth & Bone Age', text: 'AI-based bone-age and skeletal maturity assessment to track physical growth trajectory.' },
  { icon: Apple, color: 'var(--brand-purple)', accent: '#8B5CF6', title: 'Nutrition & Body Composition', text: 'Beyond weight — real body composition and nutritional status to catch faltering early.' },
  { icon: Brain, color: 'var(--brand-coral)', accent: '#FF4D7D', title: 'Cognition & Focus', text: 'Cognitive readiness, attention and focus screening for early developmental insight.' },
  { icon: Smile, color: 'var(--brand-primary)', accent: 'var(--brand-secondary)', title: 'Dental Health', text: 'Oral and dental health screening as a core, often-missed dimension of child wellbeing.' },
  { icon: PersonStanding, color: 'var(--brand-purple)', accent: '#8B5CF6', title: 'Development & Posture', text: 'Developmental milestones, posture and skeletal symmetry evaluated over time.' }
];

const objectives = [
  {
    num: '01',
    icon: ScanLine,
    color: 'var(--brand-primary)',
    accent: 'var(--brand-secondary)',
    title: 'Screen Every Child, Miss No Dimension',
    text: "Move India from reactive pediatrics (treat when sick) to preventive, whole-child screening across all five Tots360 dimensions — skeletal growth & bone age, nutrition & body composition, cognition & focus, dental health, and development & posture. No child should be assessed on weight alone when five dimensions determine their future."
  },
  {
    num: '02',
    icon: Clock,
    color: 'var(--brand-purple)',
    accent: '#8B5CF6',
    title: "Detect Early, Intervene Before It's Late",
    text: 'Shift the window of detection from "after symptoms appear" to "before they manifest." Identify growth faltering, nutritional deficits, developmental delays, and skeletal asymmetry in the first 1,000 days and through early childhood — when intervention actually changes outcomes, not just manages them.'
  },
  {
    num: '03',
    icon: Database,
    color: 'var(--brand-coral)',
    accent: '#FF4D7D',
    title: 'Data-Driven Child Health at Population Scale',
    text: "Build India's first AI-powered, population-level child health intelligence — anonymised, longitudinal growth data across districts, states, and demographics. Move from sample-based national surveys (NFHS every few years) to continuous, real-time insight on how India's children are actually growing."
  },
  {
    num: '04',
    icon: MapPinned,
    color: 'var(--brand-primary)',
    accent: 'var(--brand-secondary)',
    title: 'Bridge Urban Clinics and Underserved Communities',
    text: "Ensure that a child in a Tier-3 town or rural Anganwadi gets the same quality of screening as a child in a Hyderabad pediatric clinic. AI-powered screening, deliverable via a smartphone, is the equaliser — it doesn't need a specialist at the point of care, only at the point of interpretation."
  },
  {
    num: '05',
    icon: Users,
    color: 'var(--brand-purple)',
    accent: '#8B5CF6',
    title: 'Empower Parents, Not Just Doctors',
    text: "Make parents active participants in understanding their child's growth — not passive recipients of a doctor's verdict. Every screened child's parent should understand where their child stands across all five dimensions, what's on track, what needs attention, and what to do next."
  },
  {
    num: '06',
    icon: Trophy,
    color: 'var(--brand-coral)',
    accent: '#FF4D7D',
    title: 'Strengthen the Young Athlete Pipeline',
    text: "Identify children with exceptional skeletal, muscular, and cognitive profiles early — and connect them to structured sport and physical development pathways. India's Olympic and sporting future starts with knowing which 6-year-old has the bone structure, growth trajectory, and cognitive readiness for elite athletic development."
  },
  {
    num: '07',
    icon: Ruler,
    color: 'var(--brand-primary)',
    accent: 'var(--brand-secondary)',
    title: 'Establish National Benchmarks for Indian Children',
    text: "India still relies heavily on WHO growth charts built on international cohorts. Build India-specific growth, development, and bone-age benchmarks using Tots360's own screening data — so Indian children are measured against Indian baselines, not global averages that may not reflect local genetics, nutrition patterns, and growth trajectories."
  },
  {
    num: '08',
    icon: Network,
    color: 'var(--brand-purple)',
    accent: '#8B5CF6',
    title: "Integrate with India's Existing Health Architecture",
    text: "Don't build a parallel system — plug into what exists. Connect Tots360 screening data with RBSK (Rashtriya Bal Swasthya Karyakram) District Early Intervention Centres, Poshan Tracker, and the school-health ecosystem so that a child flagged by AI screening flows seamlessly into the public health referral and intervention chain."
  },
  {
    num: '09',
    icon: RefreshCw,
    color: 'var(--brand-coral)',
    accent: '#FF4D7D',
    title: 'Make Screening Periodic, Not One-Time',
    text: 'A single screening is a snapshot; periodic screening is a growth story. Ensure every enrolled child is re-screened at defined intervals (quarterly for under-3s, biannually for 3–10, annually for 10–18) so that trends, velocity changes, and emerging issues are caught in motion, not frozen in a moment.'
  },
  {
    num: '10',
    icon: Target,
    color: 'var(--brand-primary)',
    accent: 'var(--brand-secondary)',
    title: 'One Crore Children Screened by 2030',
    text: 'Set a measurable, time-bound target: screen one crore (10 million) children across India\'s 5 dimensions by 2030. Begin with Telangana as the proof state, expand to five states by 2028, and go national. Make "Tots360-screened" a recognisable standard that parents, schools, and sports academies ask for by name.'
  }
];

const springConfig = { type: 'spring', damping: 80, stiffness: 400, mass: 1 };

const useCountUp = (to, inView, duration = 2000) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(p * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return val;
};

const StatCounter = ({ to, suffix = '', label, color, plain = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const val = useCountUp(to, inView);
  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-5xl md:text-6xl font-bold mb-2" style={{ color }}>
        {plain ? val : val.toLocaleString('en-IN')}{suffix}
      </div>
      <p className="body-standard text-[var(--text-secondary)]">{label}</p>
    </div>
  );
};

const BalaVardhan = () => {
  React.useEffect(() => {
    document.title = "Bharat's Bala Vardhan Mission - Kodamalas Krushi Foundation";
  }, []);

  return (
    <div className="min-h-screen" data-testid="bala-vardhan-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden gradient-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[var(--brand-primary)] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-[var(--brand-coral)] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="container mx-auto px-6 md:px-8 lg:px-12 relative z-10">
          <div className="max-w-5xl mx-auto text-center mb-14">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border border-[var(--brand-primary)]/40 mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[var(--brand-primary)] animate-pulse"></span>
              <span className="body-small text-[var(--brand-primary)] font-medium">India's Child Growth &amp; Strengthening Mission</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.1 }}
              className="display-hero text-[var(--text-primary)] mb-6"
            >
              Bharat's Bala Vardhan Mission
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.2 }}
              className="body-large text-[var(--text-secondary)] mb-4"
            >
              Know Every Child. Grow Every Child. Strengthen Every Generation.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <a href="https://tots360.in/" target="_blank" rel="noopener noreferrer" data-testid="tots360-hero-link">
                <button className="btn-primary flex items-center gap-2">
                  Powered by Tots360 — AI Powered Child Care
                  <ExternalLink size={18} />
                </button>
              </a>
              <Link to="/contact#contact-form">
                <button className="btn-secondary">Get Involved</button>
              </Link>
            </motion.div>
          </div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.4 }}
            className="max-w-3xl mx-auto"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-[var(--border-light)] bg-white p-6">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_bbc9e96f-7875-4deb-b7db-7c2f321eb51d/artifacts/m8a302nx_Bharat%27s%20Bala%20Vardhan%20Mission1.png"
                alt="Bharat's Bala Vardhan Mission logo"
                className="w-full h-auto max-h-[440px] object-contain mx-auto"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass-card rounded-3xl p-10 md:p-12 border border-[var(--border-light)] text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-[var(--brand-primary)] opacity-10 rounded-full -mr-20 -mt-20 blur-3xl"></div>
            <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg shadow-[var(--brand-primary)]/30">
              <Sparkles size={32} className="text-[var(--bg-primary)]" />
            </div>
            <h2 className="heading-large text-[var(--text-primary)] mb-4 neon-green">Our Vision</h2>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              Every child in India fully understood, completely screened, and empowered to reach their full physical and cognitive potential — building the strongest generation Bharat has ever raised.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Five Dimensions Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-primary)]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">The Five Screening Dimensions</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[var(--brand-primary)] via-[var(--brand-purple)] to-[var(--brand-coral)] mx-auto rounded-full mb-6"></div>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              No child should be assessed on weight alone. Tots360 screens every child across five whole-child dimensions.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {dimensions.map((dim, index) => (
              <motion.div
                key={dim.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.08 }}
                className="glass-card rounded-3xl p-6 hover-lift border border-[var(--border-light)] text-center relative overflow-hidden"
                data-testid={`dimension-card-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-lg" style={{ background: `linear-gradient(135deg, ${dim.color} 0%, ${dim.accent} 100%)` }}>
                  <dim.icon size={26} className="text-white" />
                </div>
                <h3 className="body-large font-bold text-[var(--text-primary)] mb-2">{dim.title}</h3>
                <p className="body-small text-[var(--text-secondary)]">{dim.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-primary)]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">Ten Objectives</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[var(--brand-primary)] via-[var(--brand-purple)] to-[var(--brand-coral)] mx-auto rounded-full mb-6"></div>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              A comprehensive roadmap to screen, strengthen, and safeguard the growth of every child across Bharat.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {objectives.map((obj, index) => (
              <motion.div
                key={obj.num}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ ...springConfig, delay: (index % 2) * 0.1 }}
                className="glass-card rounded-3xl p-8 hover-lift border border-[var(--border-light)] relative overflow-hidden group"
                style={{ borderTop: `4px solid ${obj.color}` }}
                data-testid={`objective-card-${obj.num}`}
              >
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16 blur-2xl opacity-20"
                  style={{ background: obj.color }}
                ></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-5">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
                      style={{ background: `linear-gradient(135deg, ${obj.color} 0%, ${obj.accent} 100%)` }}
                    >
                      <obj.icon size={28} className="text-white" />
                    </div>
                    <span
                      className="text-4xl font-bold opacity-30"
                      style={{ color: obj.color }}
                    >
                      {obj.num}
                    </span>
                  </div>
                  <h3 className="heading-medium text-[var(--text-primary)] mb-3">
                    {obj.title}
                  </h3>
                  <p className="body-standard text-[var(--text-secondary)]">
                    {obj.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mt-14"
          >
            <p className="body-large text-[var(--text-secondary)] mb-6">
              Want to bring the mission to your school, clinic, or community?
            </p>
            <Link to="/contact#contact-form">
              <button className="btn-primary flex items-center gap-2 mx-auto" data-testid="objectives-get-in-touch-btn">
                Get in Touch <ArrowRight size={18} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Impact / Targets Counter Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-primary)] relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[var(--brand-primary)] opacity-5 rounded-full blur-3xl"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-14"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">Our 2030 Targets</h2>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              A measurable, time-bound mission — starting with Telangana as the proof state and going national.
            </p>
          </motion.div>

          <div className="glass-card rounded-3xl p-10 md:p-14 border border-[var(--border-light)] grid grid-cols-2 lg:grid-cols-4 gap-10">
            <StatCounter to={1} suffix=" Cr" label="Children Screened by 2030" color="var(--brand-primary)" />
            <StatCounter to={5} label="Dimensions Per Child" color="var(--brand-purple)" />
            <StatCounter to={5} label="States by 2028" color="var(--brand-coral)" />
            <StatCounter to={2030} label="National Standard Year" color="var(--brand-primary)" plain />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass-card rounded-3xl p-12 text-center max-w-4xl mx-auto border border-[var(--border-light)] relative overflow-hidden"
          >
            <div className="absolute inset-0 morphing-bg"></div>
            <div className="relative z-10">
              <h2 className="heading-large text-[var(--text-primary)] mb-4">
                Join the Mission to Strengthen Bharat's Children
              </h2>
              <p className="body-large text-[var(--text-secondary)] mb-8 max-w-2xl mx-auto">
                Powered by Tots360 — AI Powered Child Care. Be part of India's first population-scale, whole-child screening movement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://tots360.in/" target="_blank" rel="noopener noreferrer" data-testid="tots360-cta-link">
                  <button className="btn-primary flex items-center gap-2 mx-auto">
                    Visit Tots360 <ExternalLink size={18} />
                  </button>
                </a>
                <Link to="/contact#contact-form">
                  <button className="btn-secondary flex items-center gap-2 mx-auto">
                    Get in Touch <ArrowRight size={18} />
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BalaVardhan;
